<script setup lang="ts">
import { computed, ref } from "vue";
import { useElementWidth } from "../composables/useElementWidth";

/**
 * A bare trend line for a stat tile: zero-based, so a steady metric reads as flat,
 * with the newest value picked out by a dot. Full detail lives in the chart it opens.
 */
const props = withDefaults(defineProps<{ values: ReadonlyArray<number | null>; height?: number }>(), {
  height: 32
});

const root = ref<HTMLElement | null>(null);
const width = useElementWidth(root);
const PAD = 4;

const geometry = computed(() => {
  const count = props.values.length;
  if (width.value <= 0 || count < 2) return null;
  let max = 0;
  for (const value of props.values) if (value != null && value > max) max = value;
  if (max <= 0) max = 1;
  const inner = width.value - PAD * 2;
  const floor = props.height - 2;

  let line = "";
  let inRun = false;
  let lastX = 0;
  let lastY = 0;
  let drawn = false;
  for (let index = 0; index < count; index += 1) {
    const value = props.values[index];
    if (value == null) {
      inRun = false;
      continue;
    }
    const x = PAD + (index / (count - 1)) * inner;
    const y = floor - (value / max) * (floor - PAD);
    line += `${inRun ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    inRun = true;
    lastX = x;
    lastY = y;
    drawn = true;
  }
  return drawn ? { line, lastX, lastY } : null;
});
</script>

<template>
  <div ref="root" class="sparkline" :style="{ height: `${height}px` }" aria-hidden="true">
    <svg v-if="geometry" :width="width" :height="height" :viewBox="`0 0 ${width} ${height}`">
      <path class="sparkline__line" :d="geometry.line" />
      <circle class="sparkline__dot" :cx="geometry.lastX" :cy="geometry.lastY" r="3" />
    </svg>
  </div>
</template>

<style scoped>
.sparkline { width: 100%; min-width: 0; }
.sparkline svg { display: block; overflow: visible; }
.sparkline__line {
  fill: none;
  stroke: var(--ink-3);
  stroke-width: 1.25;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.sparkline__dot { fill: var(--accent); stroke: var(--bg); stroke-width: 1.5; }
</style>
